import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { assertIsAxiosError } from "_shared/_errors/errors";
import Guid from "_shared/guids/guid";
import axios, { AxiosHeaders, CanceledError, HttpStatusCode } from "axios";
import { MessageNotificationJSON } from "notifications/entities/message-notification/message-notification-schema";
import NotificationGroupAPIRequest from "notifications/entities/notification/api/request-contracts/notification-group-api-request";
import NotificationQuery from "notifications/entities/notification/api/request-contracts/notification-query";
import Notification from "notifications/entities/notification/notification";
import NotificationSchema, { NotificationJSON } from "notifications/entities/notification/notification-schema";
import Session from "users/__session/session";

export default class NotificationsAPIService {
  private readonly headers: AxiosHeaders = new AxiosHeaders();

  private headersWithJson(): AxiosHeaders {
    return this.headers.concat({ "Content-Type": "application/json-patch+json" });
  }

  constructor(session: Readonly<Session>) {
    if (!session.authToken) throw new Error("Cannot create NotificationsAPIService without session.");
    this.headers.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  async markNotificationAsSeen(notification: Partial<Notification>): Promise<Notification | null | undefined> {
    if (!notification.id) throw new Error("Notification id is required");

    try {
      const url = new URL(`/notifications/${notification.id.value}/mark-seen`, AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.headers,
        },
      );
      return Notification.fromJSON(response.data);
    } catch (error) {
      assertIsAxiosError(error);
      if (error?.response?.status === HttpStatusCode.NotFound) {
        throw new NotificationNotFoundError(notification.id);
      }
      throw new NotificationsAPIServiceError("markNotificationAsSeen", error);
    }
  }

  async markNotificationsAsSeen(notifications: Partial<Notification>[]): Promise<Notification[] | null | undefined> {
    for (const notification of notifications) {
      if (!notification.id) throw new Error("Notification id is required");
    }

    try {
      const url = new URL(`/notifications/mark-seen`, AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.post(
        url.toString(),
        notifications.map((notification) => notification.id?.value),
        { headers: this.headers },
      );
      return response.data.map((notificationData: NotificationJSON) => Notification.fromJSON(notificationData));
    } catch (error) {
      assertIsAxiosError(error);
      throw new NotificationsAPIServiceError("markNotificationsAsSeen", error);
    }
  }

  async markAllNotificationsAsSeen(): Promise<void> {
    const url = new URL(`/notifications/mark-all-seen`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.post(url.toString(), {}, { headers: this.headers });
    } catch (error) {
      assertIsAxiosError(error);
      if (error?.response?.status === HttpStatusCode.NotFound) {
        throw new UserNotificationsNotFoundError();
      }
      throw new NotificationsAPIServiceError("markAllNotificationsAsSeen", error);
    }
  }

  async markNotificationAsUnSeen(notification: Partial<Notification>): Promise<Notification | null | undefined> {
    if (!notification.id) throw new Error("Notification id is required");

    try {
      const url = new URL(`/notifications/${notification.id.value}/mark-unseen`, AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.post(url.toString(), {}, { headers: this.headers });
      return Notification.fromJSON(response.data);
    } catch (error) {
      assertIsAxiosError(error);
      if (error?.response?.status === HttpStatusCode.NotFound) {
        throw new NotificationNotFoundError(notification.id);
      }
      throw new NotificationsAPIServiceError("markNotificationAsUnSeen", error);
    }
  }

  async markNotificationsAsUnSeen(notifications: Partial<Notification>[]): Promise<Notification[] | null | undefined> {
    for (const notification of notifications) {
      if (!notification.id) throw new Error("Notification id is required");
    }

    try {
      const url = new URL(`/notifications/mark-unseen`, AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.post(
        url.toString(),
        notifications.map((notification) => notification.id?.value),
        { headers: this.headers },
      );
      return response.data.map((notificationData: NotificationJSON) => Notification.fromJSON(notificationData));
    } catch (error) {
      assertIsAxiosError(error);
      throw new NotificationsAPIServiceError("markNotificationsAsUnSeen", error);
    }
  }

  async markNotificationGroupAsSeen(groupInfo: NotificationGroupAPIRequest): Promise<void> {
    const url = new URL(`/notifications/mark-group-seen`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.post(url.toString(), groupInfo, {
        headers: this.headersWithJson(),
      });
    } catch (error) {
      assertIsAxiosError(error);
      throw new NotificationsAPIServiceError("markNotificationGroupAsSeen", error);
    }
  }

  async archiveNotification(notification: Partial<Notification>): Promise<void> {
    if (!notification.id) throw new Error("Notification id is required");

    try {
      const url = new URL(`/notifications/${notification.id.value}/archive`, AttorneyHubAPIService.apiBaseUrl);
      await axios.post(
        url.toString(),
        {},
        {
          headers: this.headers,
        },
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error?.response?.status === HttpStatusCode.NotFound) {
        throw new NotificationNotFoundError(notification.id);
      }
      throw new NotificationsAPIServiceError("archiveNotification", error);
    }
  }

  async unArchiveNotification(notification: Partial<Notification>): Promise<void> {
    if (!notification.id) throw new Error("Notification id is required");

    try {
      const url = new URL(`/notifications/${notification.id.value}/unarchive`, AttorneyHubAPIService.apiBaseUrl);
      await axios.post(
        url.toString(),
        {},
        {
          headers: this.headers,
        },
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error?.response?.status === HttpStatusCode.NotFound) {
        throw new NotificationNotFoundError(notification.id);
      }
      throw new NotificationsAPIServiceError("unArchiveNotification", error);
    }
  }

  async getNotificationById(notificationId: Guid, abortController?: AbortController): Promise<Notification> {
    try {
      const url = new URL(`/notifications/${notificationId.value}`, AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: abortController?.signal,
      });
      return new Notification(NotificationSchema.deserialize(response.data));
    } catch (error) {
      assertIsAxiosError(error);
      if (error?.response?.status === HttpStatusCode.NotFound) {
        throw new NotificationNotFoundError(notificationId);
      }
      throw new NotificationsAPIServiceError("getNotificationById", error);
    }
  }

  async getNotificationsByQuery(
    query: NotificationQuery,
    abortController?: AbortController,
  ): Promise<PaginatedResponse<NotificationJSON | MessageNotificationJSON>> {
    try {
      const response = await axios.get(
        `${AttorneyHubAPIService.apiBaseUrl}/notifications?${query.searchParams.toString()}`,
        {
          headers: this.headers,
          signal: abortController?.signal,
        },
      );

      return PaginatedResponse.from<NotificationJSON | MessageNotificationJSON>(
        response.data,
        response.headers["x-pagination"],
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error?.response?.status === HttpStatusCode.NotFound) throw new UserNotificationsNotFoundError();
      if (error instanceof CanceledError) throw error;
      throw new NotificationsAPIServiceError("queryNotifications", error);
    }
  }
}

// #region Exceptions

export class NotificationsAPIServiceError extends Error {
  public methodName: string;
  public error: Error;

  constructor(methodName: string, error: Error) {
    super(`Error in NotificationsAPIService.${methodName}: ${error}`);
    this.methodName = methodName;
    this.error = error;
  }
}

export class NotificationNotFoundError extends Error {
  public readonly id: Guid;

  constructor(id: Guid) {
    super(`Unable to find notification by id: ${id}`);
    this.id = id;
  }
}

export class UserNotificationsNotFoundError extends Error {
  constructor() {
    super(`User does not have any notifications`);
  }
}

// #endregion
